import { Box, Grid2 } from '@mui/material';
import DisconnectIcon from '@assets/images/disconnect.svg';
import NoconnectIcon from '@assets/images/noconnection.svg';
import SessionIcon from '@assets/images/sessionIcon.svg';
import CustomTypography from '@qler-view/shared_lib/src/shared/lib/ui-components/Typography/CustomTypography';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  PORTAL_TYPES,
} from '@qler-view/shared_lib/src';
import CancelConsultModel, {
  type CancelReasonFacilityParams,
  type CancelReasonFacilitySubmitParams,
} from '@qler-view/shared_lib/src/shared/statusrequests/CancelConsultModel';
import { useEffect, useState } from 'react';
import {
  useCancelConsultMetadata,
  useSaveCancelConsult,
} from '../../shared/statusRequest/StatusRequestSetup';
import { useAuth } from '../components/AuthContext';
import { getMaskedOrOriginal } from '../components/phiMasking';
import {
  UseGetAbandonedOrCancelConsult,
  useReSubmitConsult,
  useUpdateAbandonedOrCancelConsult,
} from '../../API/hooks/reSubmitOrCancelConsultHook';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
  CONSULT_WAITING_SCREEN_ROUTE_PATH,
  FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
} from '../../routers/RoutPath';
import { stopAlert } from '../../shared/lib/utils/audioManager';
import { useUpdateConsultStatus } from '../../API/hooks/waitingHook';
import { CallStatusEnum } from '../components/ConsultStatusChip';
import { ROLES } from '../../roles';

const ReSubmitOrCancelConsult = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [openCancelRequest, setOpenCancelRequest] = useState(false);
  const [showCompletedPopup, setShowCompletedPopup] = useState(false);
  const consultId = location.state?.consultId;
  const { CancelConsultMetadata: ConsultCancelMetaData } =
    useCancelConsultMetadata(PORTAL_TYPES.FACILITY);
  const { AbandonedOrCancelResponse, isLoading } =
    UseGetAbandonedOrCancelConsult(consultId);

  const { consultStatus } = useUpdateConsultStatus(Number(consultId) || 0);

  const isStatusChanged =
    consultStatus?.status?.toLowerCase() === CallStatusEnum.OnHold ||
    consultStatus?.status?.toLowerCase() === CallStatusEnum.New ||
    consultStatus?.status?.toLowerCase() === CallStatusEnum.InProgress;

  const isStatusCancelled =
    consultStatus?.status?.toLowerCase() === CallStatusEnum.Cancelled;

  const isStatusCompleted =
    consultStatus?.status?.toLowerCase() === CallStatusEnum.Completed;

  const isPHIMaskingEnabled =
    AbandonedOrCancelResponse?.isHidePhiAfterCompletion;

  useEffect(() => {
    if (isStatusChanged) {
      navigate(`${CONSULT_WAITING_SCREEN_ROUTE_PATH}?consultId=${consultId}`, {
        replace: true,
      });
    }
    if (isStatusCancelled) {
      const dashboardPath =
        user?.role?.toString() === ROLES.CASE_MANAGER
          ? CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH
          : FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH;
      navigate(dashboardPath, {
        replace: true,
      });
    }
  }, [consultStatus, consultId, navigate, user]);

  useEffect(() => {
    stopAlert();
  }, []);

  const onSuccess = () => {
    navigate(`${CONSULT_WAITING_SCREEN_ROUTE_PATH}?consultId=${consultId}`, {
      replace: true,
    });
  };

  const { mutate: cancelConsult } = useSaveCancelConsult();
  const { mutate: reSubmit } = useReSubmitConsult(onSuccess);
  const { mutate: updateAbadonedOrCancelConsult } =
    useUpdateAbandonedOrCancelConsult(() => {});

  const handleSubmitCancelReason = (data: CancelReasonFacilityParams) => {
    const mappedTechnicalDifficultiesId =
      [
        data.patientAdmittedId,
        data.noLongerRequiredId,
        data.patientDischargedId,
        data.technicalDifficultiesId,
      ].find((id) => id !== 0) ?? 0;

    const payload: CancelReasonFacilitySubmitParams = {
      cancelReasonId: data.cancelReasonId,
      technicalDifficultiesId: mappedTechnicalDifficultiesId,
      otherReason: data.otherReason,
    };
    cancelConsult(
      {
        userId: user?.userId.toString() ?? '',
        portalType: PORTAL_TYPES.FACILITY,
        consultId: location.state?.consultId,
        ...payload,
      },
      {
        onSuccess: () => {
          navigate(FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH, {
            replace: true,
          });
          setOpenCancelRequest(false);
        },
      }
    );
  };

  const reSubmitConsultForm = () => {
    reSubmit({
      userId: user?.userId.toString() ?? '',
      consultId: location.state?.consultId,
    });
  };

  const handleUpdateAndRedirect = (action: string) => {
    updateAbadonedOrCancelConsult(
      {
        userId: user?.userId.toString() ?? '',
        consultId: location.state?.consultId,
        action: action,
      },
      {
        onSuccess: () => {
          const dashboardPath =
            user?.role?.toString() === ROLES.CASE_MANAGER
              ? CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH
              : FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH;
          navigate(dashboardPath, { replace: true });
        },
      }
    );
  };

  return (
    <Box>
      {openCancelRequest && (
        <CancelConsultModel
          openCancelConsultPopup={openCancelRequest}
          onCloseCancelConsultPopup={() => setOpenCancelRequest(false)}
          ConsultCancelMetaData={ConsultCancelMetaData}
          onSubmitCancelConsult={(values) => {
            handleSubmitCancelReason(values);
          }}
          portal={PORTAL_TYPES.FACILITY}
        />
      )}
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CustomTypography
            id="loading-text"
            variant="h5"
            className="text-align-center pt-32"
            color="textDisabled"
          >
            Loading...
          </CustomTypography>
        </Box>
      ) : (
        <Box className="pt-32">
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Box className="flex items-center justify-center pb-16">
                {AbandonedOrCancelResponse?.errorType ? (
                  <DisconnectIcon />
                ) : (
                  <NoconnectIcon />
                )}
              </Box>
              {AbandonedOrCancelResponse?.errorType === 'abandoned' ? (
                <Box className="text-center">
                  <CustomTypography id="error-msg" variant="h2" color="error">
                    Uh oh, it looks like your device disconnected from our
                    service! This can happen if your device goes to sleep, the
                    screen locks on your iPad, or internet connectivity is lost
                    for longer than a minute. You can click the resubmit button
                    and you will resume your place in line. If the consult is no
                    longer needed, please click “Cancel Request” and select the
                    reason for cancellation.
                  </CustomTypography>
                </Box>
              ) : (
                <Box className="text-center">
                  <CustomTypography id="error-msg" variant="h2" color="error">
                    Sorry we missed you! To resubmit the consultation please
                    click on ‘Resubmit’ and your request will be returned to the
                    queue. Otherwise click on ‘Close’ to return to home screen.
                  </CustomTypography>
                </Box>
              )}

              <Box className="pt-24">
                <Grid2 container>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                    <Box className="text-align-center">
                      <Box className="pb-24">
                        <CustomTypography
                          id="waiting-patient-name"
                          variant="h3"
                          color="textPrimary"
                        >
                          {getMaskedOrOriginal(
                            AbandonedOrCancelResponse?.patientFirstName,
                            AbandonedOrCancelResponse?.patientLastName,
                            isPHIMaskingEnabled
                          )}
                        </CustomTypography>
                      </Box>

                      <Box className="display-session-id">
                        <SessionIcon />
                        <CustomTypography id="session-id-text" variant="h6">
                          Session ID -{' '}
                          <span className="consultid">
                            {AbandonedOrCancelResponse?.sessionId}
                          </span>
                        </CustomTypography>
                      </Box>
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>

              <Box className="flex justify-center pt-24">
                <Box className="pr-16">
                  <CustomButton
                    id="resubmit-button"
                    variant="contained"
                    onClick={() => {
                      if (isStatusCompleted) {
                        setShowCompletedPopup(true);
                        return;
                      }
                      reSubmitConsultForm();
                    }}
                  >
                    Resubmit
                  </CustomButton>
                </Box>
                {AbandonedOrCancelResponse?.errorType === 'abandoned' && (
                  <Box>
                    <CustomButton
                      id="cancel-request-button"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setOpenCancelRequest(true);
                      }}
                    >
                      Cancel Request
                    </CustomButton>
                  </Box>
                )}
                {AbandonedOrCancelResponse?.errorType === 'missed_call' && (
                  <Box>
                    <CustomButton
                      id="cancel-request-button"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        handleUpdateAndRedirect('close');
                      }}
                    >
                      Close
                    </CustomButton>
                  </Box>
                )}
              </Box>
            </Grid2>
          </Grid2>
        </Box>
      )}
      {showCompletedPopup && (
        <CustomDialog
          id="consult-completed-dialog"
          open={showCompletedPopup}
          onClose={(reason) => {
            if (reason === 'backdropClick') {
              return;
            }
          }}
          disableEscapeKeyDown={true}
        >
          <CustomDialogContent className="dialog-width">
            <Box>
              <Box className="pb-8">
                <CustomTypography id="consult-completed-title" variant="h4">
                  Alert
                </CustomTypography>
              </Box>
              <Box>
                <CustomTypography
                  id="consult-completed-message"
                  variant="body3"
                >
                  This consult has been Completed and cannot be resubmitted.
                </CustomTypography>
              </Box>
            </Box>
          </CustomDialogContent>
          <CustomDialogActions>
            <CustomButton
              id="consult-completed-ok-button"
              color="primary"
              variant="contained"
              onClick={() => {
                setShowCompletedPopup(false);
                handleUpdateAndRedirect('ok');
              }}
            >
              Ok
            </CustomButton>
          </CustomDialogActions>
        </CustomDialog>
      )}
    </Box>
  );
};

export default ReSubmitOrCancelConsult;
